import { APP_NAME, APP_SUBTITLE, DEVELOPER_NAME } from '@idle-depths/shared';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Card, CardContent, Spinner } from '@/components/ui';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores';

/**
 * Abyssal Beacon logo component - the game's main logo.
 * Features a glowing crystal/beacon descending into depth rings.
 */
function AbyssalBeaconLogo(): React.ReactElement {
  return (
    <div className="relative h-28 w-28">
      {/* Outer glow effect */}
      <div className="absolute inset-0 rounded-full bg-biolume-cyan/20 blur-xl animate-pulse-slow" />

      {/* Main logo container */}
      <div className="relative h-full w-full rounded-full bg-gradient-to-b from-abyss-dark to-abyss-void p-1 shadow-[0_0_30px_rgba(34,211,238,0.3)]">
        <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-b from-abyss-mid to-abyss-void border border-biolume-cyan/20">
          {/* Depth rings */}
          <div className="absolute inset-4 rounded-full border border-biolume-cyan/10" />
          <div className="absolute inset-6 rounded-full border border-biolume-cyan/15" />
          <div className="absolute inset-8 rounded-full border border-biolume-cyan/20" />

          {/* Crystal/Beacon SVG */}
          <svg
            className="h-16 w-16 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]"
            viewBox="0 0 24 24"
            fill="none"
          >
            <defs>
              <linearGradient id="crystalGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#67e8f9" />
                <stop offset="50%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#0891b2" />
              </linearGradient>
              <radialGradient id="coreGlow" cx="50%" cy="35%" r="50%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="50%" stopColor="#67e8f9" />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* Main crystal shape */}
            <path d="M12 2L15 9L12 22L9 9L12 2Z" fill="url(#crystalGrad)" />
            {/* Glowing core */}
            <ellipse cx="12" cy="9" rx="2.5" ry="3" fill="url(#coreGlow)" />
            <ellipse cx="12" cy="9" rx="1" ry="1.5" fill="white" opacity="0.9" />
          </svg>
        </div>
      </div>
    </div>
  );
}

/**
 * Connection status indicator component.
 */
function ConnectionIndicator(): React.ReactElement {
  const appStore = useAppStore();

  const statusConfig = {
    connecting: {
      color: 'bg-treasure-amber',
      text: 'Connecting...',
      pulse: true,
    },
    connected: {
      color: 'bg-game-stamina',
      text: 'Connected',
      pulse: false,
    },
    disconnected: {
      color: 'bg-muted-foreground',
      text: 'Disconnected',
      pulse: false,
    },
    error: {
      color: 'bg-game-health',
      text: 'Connection Error',
      pulse: false,
    },
  };

  const config = statusConfig[appStore.connectionStatus];

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <span className={cn('h-2 w-2 rounded-full', config.color, config.pulse && 'animate-pulse')} />
      <span>{config.text}</span>
      {appStore.lastHealthCheck && appStore.isConnected && (
        <span className="text-xs opacity-60">
          ({appStore.lastHealthCheck.database.latencyMs}ms)
        </span>
      )}
    </div>
  );
}

const ObservedConnectionIndicator = observer(ConnectionIndicator);

/**
 * Splash page component displayed on application load.
 *
 * Shows:
 * - Game title and subtitle with glowing effects
 * - Abyssal Beacon logo
 * - Connection status to backend
 * - Version information
 * - Error messages if connection fails
 */
function SplashPage(): React.ReactElement {
  const appStore = useAppStore();

  // Health check query with automatic retries
  const healthQuery = trpc.health.check.useQuery(undefined, {
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  // Update store based on query state
  useEffect(() => {
    if (healthQuery.isLoading) {
      appStore.setConnectionStatus('connecting');
    } else if (healthQuery.isError) {
      const errorMessage = healthQuery.error.message || 'Unknown error';
      appStore.handleConnectionFailure(new Error(errorMessage));
    } else if (healthQuery.data) {
      appStore.setHealthCheckResult(healthQuery.data);
      appStore.setLoading(false);
    }
  }, [healthQuery.isLoading, healthQuery.isError, healthQuery.data, healthQuery.error, appStore]);

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center p-4">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/assets/background-image-1.png)' }}
      />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-abyss-void/60" />
      {/* Bottom vignette for footer readability */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, transparent 50%, rgba(2,6,23,0.8) 100%)',
        }}
      />

      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top glow */}
        <div className="absolute -top-1/4 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-biolume-cyan/5 blur-3xl" />
        {/* Bottom glow */}
        <div className="absolute -bottom-1/4 left-1/4 h-96 w-96 rounded-full bg-rarity-epic/5 blur-3xl" />
        {/* Right accent */}
        <div className="absolute top-1/3 -right-20 h-64 w-64 rounded-full bg-treasure-gold/5 blur-3xl" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-8 animate-fade-in">
        {/* Logo */}
        <div className="animate-slide-up">
          <AbyssalBeaconLogo />
        </div>

        {/* Title with glow effect */}
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="font-display text-5xl font-bold tracking-wide text-foreground animate-slide-up animation-delay-100 drop-shadow-[0_0_40px_rgba(34,211,238,0.4)] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            {APP_NAME}
          </h1>
          <p className="font-display text-xl tracking-[0.3em] uppercase text-biolume-cyan/70 animate-slide-up animation-delay-200 drop-shadow-[0_0_15px_rgba(34,211,238,0.3)]">
            {APP_SUBTITLE}
          </p>
        </div>

        {/* Status card */}
        <Card className="w-full max-w-sm animate-slide-up animation-delay-300">
          <CardContent className="flex flex-col items-center gap-4 p-6">
            <ObservedConnectionIndicator />

            {appStore.errorMessage && (
              <div className="w-full rounded-md bg-destructive/10 border border-destructive/30 p-3 text-center text-sm text-destructive">
                {appStore.errorMessage}
              </div>
            )}

            {appStore.isConnecting && (
              <div className="flex flex-col items-center gap-3">
                <Spinner size="lg" />
                <div className="flex flex-col items-center gap-1">
                  <span className="text-sm text-muted-foreground">Establishing connection...</span>
                  <span className="text-xs text-muted-foreground/50">Preparing your adventure</span>
                </div>
              </div>
            )}

            {appStore.isConnected && (
              <div className="flex flex-col items-center gap-3">
                <p className="text-center text-sm text-muted-foreground leading-relaxed">
                  Descend into the mysterious abyss where ancient treasures and forgotten
                  civilizations await discovery. Train your skills, battle creatures of the deep,
                  and uncover the secrets lurking beneath the waves.
                </p>
                <p className="text-center text-xs font-medium text-biolume-cyan/80 tracking-wide">
                  Beta access coming Spring 2026
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div
          className="flex flex-col items-center gap-1 text-xs text-muted-foreground animate-slide-up animation-delay-400"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7)' }}
        >
          <span>v{appStore.version}</span>
          <span className="text-treasure-gold/80">{DEVELOPER_NAME}</span>
        </div>
      </div>
    </div>
  );
}

export default observer(SplashPage);
